import {
  GetMyUpcomingReservationsResponseDto,
  ReservationQuestionDto,
} from "@/api/generated/generatedTypes";
import { EventReservationFormValues } from "@/pages/EventReservation.page";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMemo, useState } from "react";
import { DateLocationQuestionnaire } from "./DateLocationQuestionnaire/DateLocationQuestionnaire";
import { DinnerQuestionnaire } from "./DinnerQuestionnaire/DinnerQuestionnaire";
import { HowItWorksBeta } from "./HowItWorks/HowItWorksBeta";
import { PaymentPhase } from "./PaymentPhase/PaymentPhase";
import { usePortonePayment } from "./usePortonePayment";
import { useCompletePortoneReservation } from "./useCompletePortoneReservation";

interface EventReservationProps {
  eventQuestions: ReservationQuestionDto[];
  latestReservation: GetMyUpcomingReservationsResponseDto | null;
}
enum ReservationPhase {
  DATE_LOCATION,
  DINNER_PREFERENCES,
  HOW_IT_WORKS,
  PAYMENT,
}

// There is some tech debt here as this is really similar to PersonalityTest.tsx
// The reasoning is that PersonalityTest are questions with answers that are backed by the single questions and answers database
// reservation questions are different in that the questions and answers are sourced from various different sources (e.g. locations, flat text etc.)
// so for now, this component will be a bit of its own thing.
export function EventReservation({ eventQuestions, latestReservation }: EventReservationProps) {
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

  // Split questions into date/location vs dinner questions
  const dateLocationQuestions = useMemo(() => {
    return eventQuestions.filter((q) => q.slug === "where" || q.slug === "when");
  }, [eventQuestions]);

  const dinnerQuestions = useMemo(() => {
    return eventQuestions.filter((q) => q.slug !== "where" && q.slug !== "when");
  }, [eventQuestions]);

  const initialValues = useMemo<EventReservationFormValues>(() => {
    const baseValues: EventReservationFormValues = {
      event: {
        preferredTimes: [],
        preferredLocationIds: [],
        messageForGuests: latestReservation?.messageForGuests || "",
      },
      dinnerPreference: {
        preferredLanguage: (latestReservation?.preferredLanguage as any) || "",
        preferredCost: (latestReservation?.preferredCost as any) || "",
        dietaryRestrictions: (latestReservation?.dietaryRestrictionSlugs as any) || [],
        dietaryRestrictionsOtherText: latestReservation?.dietaryRestrictionsOtherText || "",
      },
    };
    return baseValues;
  }, [latestReservation]);

  const form = useForm<EventReservationFormValues>({
    initialValues,
  });

  const [currentReservationPhase, setCurrentReservationPhase] = useState<ReservationPhase>(
    ReservationPhase.DATE_LOCATION
  );
  const { requestPayment } = usePortonePayment();
  const { completeReservation } = useCompletePortoneReservation();

  const handleMakeReservation = async () => {
    setIsSubmittingPayment(true);
    if (
      !form.values.event.preferredLocationIds ||
      form.values.event.preferredLocationIds.length === 0
    ) {
      notifications.show({
        title: "입력 확인 필요",
        message: "최소 1개의 장소를 선택해주세요",
        color: "red",
      });
      setIsSubmittingPayment(false);
      return;
    }
    if (!form.values.event.preferredTimes || form.values.event.preferredTimes.length === 0) {
      notifications.show({
        title: "입력 확인 필요",
        message: "최소 1개의 날짜와 시간을 선택해주세요",
        color: "red",
      });
      setIsSubmittingPayment(false);
      return;
    }

    // Save reservation data to session storage before payment redirect
    const reservationData = {
      preferredLocationIds: form.values.event.preferredLocationIds,
      preferredTimes: form.values.event.preferredTimes,
      messageForGuests: form.values.event.messageForGuests,
      dinnerPreference: form.values.dinnerPreference,
    };
    sessionStorage.setItem("pendingReservation", JSON.stringify(reservationData));

    try {
      const paymentId = await requestPayment({
        orderName: "오프 디너 큐레이션",
        redirectUrl: `${window.location.origin}/payment/success`,
      });
      await completeReservation(paymentId);
    } catch (error) {
      setIsSubmittingPayment(false);
      console.error(error);
      // Clean up session storage on error
      sessionStorage.removeItem("pendingReservation");
      notifications.show({
        title: "결제 요청 실패",
        message: error instanceof Error ? error.message : "결제 요청에 실패했습니다",
        color: "red",
      });
    }
  };

  const goToDateLocationPhase = () => {
    setCurrentReservationPhase(ReservationPhase.DATE_LOCATION);
  };

  const goToDinnerPreferencesPhase = () => {
    setCurrentReservationPhase(ReservationPhase.DINNER_PREFERENCES);
  };

  const goToHowItWorksPhase = () => {
    setCurrentReservationPhase(ReservationPhase.HOW_IT_WORKS);
  };

  const goToPaymentPhase = () => {
    setCurrentReservationPhase(ReservationPhase.PAYMENT);
  };

  const totalQuestionSteps = dateLocationQuestions.length + dinnerQuestions.length;

  if (currentReservationPhase === ReservationPhase.DATE_LOCATION) {
    return (
      <DateLocationQuestionnaire
        eventQuestions={dateLocationQuestions}
        form={form}
        onQuestionnaireComplete={goToDinnerPreferencesPhase}
        totalSteps={totalQuestionSteps}
        stepOffset={0}
      />
    );
  }

  if (currentReservationPhase === ReservationPhase.DINNER_PREFERENCES) {
    return (
      <DinnerQuestionnaire
        eventQuestions={dinnerQuestions}
        form={form}
        onQuestionnaireComplete={goToHowItWorksPhase}
        onBackToDateLocation={goToDateLocationPhase}
        latestReservation={latestReservation}
        totalSteps={totalQuestionSteps}
        stepOffset={dateLocationQuestions.length}
      />
    );
  }

  if (currentReservationPhase === ReservationPhase.HOW_IT_WORKS) {
    const selectedDate =
      form.values.event.preferredTimes && form.values.event.preferredTimes.length > 0
        ? new Date(form.values.event.preferredTimes[0])
        : new Date();
    return (
      <HowItWorksBeta
        selectedDate={selectedDate}
        onNextButtonClick={goToPaymentPhase}
        onBackButtonClick={goToDinnerPreferencesPhase}
      />
    );
  }

  const locationQuestion = eventQuestions.find((question) => question.slug === "where");
  const selectedLocationIds = form.values.event.preferredLocationIds;
  const selectedLocationNames = locationQuestion?.options?.reduce((acc, option) => {
    if (selectedLocationIds.includes(option.value)) {
      acc.push(option.displayText);
    }
    return acc;
  }, [] as string[]);

  // not for regular user use. only use for development purposes
  if (currentReservationPhase === ReservationPhase.PAYMENT) {
    const devBypassReservationData = {
      preferredLocationIds: form.values.event.preferredLocationIds,
      preferredTimes: form.values.event.preferredTimes,
      messageForGuests: form.values.event.messageForGuests,
      dinnerPreference: form.values.dinnerPreference,
    };

    return (
      <>
        <PaymentPhase
          onPaymentComplete={handleMakeReservation}
          isLoading={isSubmittingPayment}
          onBackButtonClick={goToHowItWorksPhase}
          selectedDates={form.values.event.preferredTimes}
          selectedLocationNames={selectedLocationNames || []}
          devBypassReservationData={devBypassReservationData}
        />
      </>
    );
  }

  return null;
}
